import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select, { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import ControlledSubstanceFields, { ControlledSubstanceData, EMPTY_CONTROLLED_SUBSTANCE } from "./ControlledSubstanceFields";
import VacationFields, { VacationData, EMPTY_VACATION } from "./VacationFields";
import OverrideDetailsFields, { OverrideDetailsData, EMPTY_OVERRIDE_DETAILS } from "./OverrideDetailsFields";
import { ApprovalReason, RequesterRole } from "../../../consts/ApprovalsAndOverrides";
import { PrescriptionOption } from "./ConsumptionGuardOverridePage";

export interface OverrideFormData {
  reason: string;
  requesterRole: string;
  notes: string;
  includeControlledSubstanceDetails: boolean;
  controlledSubstanceDetails: ControlledSubstanceData | null;
  vacationDetails: VacationData | null;
  overrideDetails: OverrideDetailsData;
  autoOverrideApproval: boolean;
  skipApprovalService: boolean;
}

export const EMPTY_FORM_DATA: OverrideFormData = {
  reason: "",
  requesterRole: "",
  notes: "",
  includeControlledSubstanceDetails: false,
  controlledSubstanceDetails: null,
  vacationDetails: null,
  overrideDetails: EMPTY_OVERRIDE_DETAILS,
  autoOverrideApproval: false,
  skipApprovalService: false,
};

interface Props {
  formData: OverrideFormData;
  onChange: (updated: OverrideFormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  prescriptions: PrescriptionOption[];
  selectedDrugAsin: string;
}

const REASON_OPTIONS: SelectProps.Option[] = [
  { label: "Vacation Supply", value: ApprovalReason.VACATION_SUPPLY },
  { label: "Lost Medication", value: ApprovalReason.LOST_MEDICATION },
  { label: "Emergency", value: ApprovalReason.EMERGENCY },
  { label: "Dose Change", value: ApprovalReason.DOSE_CHANGE },
  { label: "On Hand Supply Adjustment", value: ApprovalReason.ON_HAND_SUPPLY_ADJUSTMENT },
];

const ROLE_OPTIONS: SelectProps.Option[] = [
  { label: "Customer", value: RequesterRole.CUSTOMER },
  { label: "Generalist", value: RequesterRole.GENERALIST },
  { label: "Pharmacist", value: RequesterRole.PHARMACIST },
  { label: "Technician", value: RequesterRole.TECHNICIAN },
];

class CreateOverrideForm extends React.Component<Props> {
  render() {
    const { formData, onChange, onSubmit, isSubmitting, prescriptions, selectedDrugAsin } = this.props;

    const selectedReason = REASON_OPTIONS.find((o) => o.value === formData.reason) || null;
    const selectedRole = ROLE_OPTIONS.find((o) => o.value === formData.requesterRole) || null;
    const canSubmit = !!formData.reason && !!formData.requesterRole;
    const showVacation = formData.reason === ApprovalReason.VACATION_SUPPLY;

    return (
      <Container header={
        <Header
          variant="h3"
          actions={
            <Button
              variant="primary"
              onClick={onSubmit}
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              Submit Override
            </Button>
          }
        >
          Create Approval and Override
        </Header>
      }>
        <SpaceBetween size="l">
          <ColumnLayout columns={2}>
            <FormField label="Reason" description="Required">
              <Select
                selectedOption={selectedReason}
                onChange={({ detail }) => {
                  const newReason = detail.selectedOption.value || "";
                  const updatedVacation = newReason === ApprovalReason.VACATION_SUPPLY
                    ? (formData.vacationDetails || { ...EMPTY_VACATION })
                    : null;
                  // Auto-populate replacedOrderId from latest fill when LOST_MEDICATION selected
                  let updatedOverrideDetails = formData.overrideDetails;
                  if (newReason === ApprovalReason.LOST_MEDICATION && !formData.overrideDetails.replacedOrderId) {
                    const drugPrescriptions = prescriptions.filter((p) => p.drugAsin === selectedDrugAsin);
                    const withOrderId = drugPrescriptions.find((p) => p.orderId);
                    updatedOverrideDetails = { ...formData.overrideDetails, replacedOrderId: withOrderId?.orderId || "" };
                  }
                  onChange({ ...formData, reason: newReason, vacationDetails: updatedVacation, overrideDetails: updatedOverrideDetails });
                }}
                options={REASON_OPTIONS}
                placeholder="Select reason..."
              />
            </FormField>
            <FormField label="Requester Role" description="Required">
              <Select
                selectedOption={selectedRole}
                onChange={({ detail }) => onChange({ ...formData, requesterRole: detail.selectedOption.value || "" })}
                options={ROLE_OPTIONS}
                placeholder="Select role..."
              />
            </FormField>
          </ColumnLayout>

          <FormField label="Notes" description="Optional">
            <Textarea
              value={formData.notes}
              onChange={({ detail }) => onChange({ ...formData, notes: detail.value })}
              placeholder="Justification notes..."
              rows={3}
            />
          </FormField>

          <Toggle
            checked={formData.autoOverrideApproval}
            onChange={({ detail }) => onChange({ ...formData, autoOverrideApproval: detail.checked })}
          >
            Automatic Override Approval
          </Toggle>

          <Toggle
            checked={formData.skipApprovalService}
            onChange={({ detail }) => onChange({ ...formData, skipApprovalService: detail.checked })}
          >
            Skip Approval Service (create UCS override directly)
          </Toggle>

          <Toggle
            checked={formData.includeControlledSubstanceDetails}
            onChange={({ detail }) => onChange({
              ...formData,
              includeControlledSubstanceDetails: detail.checked,
              controlledSubstanceDetails: detail.checked ? (formData.controlledSubstanceDetails || { ...EMPTY_CONTROLLED_SUBSTANCE }) : null,
            })}
          >
            Include Controlled Substance Details
          </Toggle>

          {formData.includeControlledSubstanceDetails && (
            <ControlledSubstanceFields
              data={formData.controlledSubstanceDetails || EMPTY_CONTROLLED_SUBSTANCE}
              onChange={(updated) => onChange({ ...formData, controlledSubstanceDetails: updated })}
            />
          )}

          {showVacation && (
            <VacationFields
              data={formData.vacationDetails || EMPTY_VACATION}
              onChange={(updated) => onChange({ ...formData, vacationDetails: updated })}
            />
          )}

          <OverrideDetailsFields
            reason={formData.reason}
            data={formData.overrideDetails}
            onChange={(updated) => onChange({ ...formData, overrideDetails: updated })}
            prescriptions={prescriptions}
            selectedDrugAsin={selectedDrugAsin}
          />
        </SpaceBetween>
      </Container>
    );
  }
}

export default CreateOverrideForm;
